import {
  type Conversation,
  type ConversationSyncItem,
  chatApi,
  type SyncedConversation,
} from "@/features/chat/api/chatApi";
import { MAX_SYNC_CONVERSATIONS } from "@/features/chat/constants";
import { db, type IConversation, type IMessage } from "@/lib/db/chatDb";
import { useChatStore } from "@/stores/chatStore";
import {
  hasAnyLiveTurn,
  isConversationStreamingNow,
  shouldBlockSyncForConversation,
} from "@/stores/streamStore";
import type { MessageType } from "@/types/features/convoTypes";

// When a remote message overwrites an existing local one, carry forward a
// non-empty local tool_data if the remote copy lacks it. Executor tool cards are
// produced client-side-first during streaming and saved locally on abort; the
// backend's cancelled/partial copy may not have caught up yet, and a wholesale
// "remote wins" replacement would otherwise delete the cards the user already saw.
const withPreservedToolData = (remote: IMessage, local: IMessage): IMessage => {
  const remoteHasToolData =
    Array.isArray(remote.tool_data) && remote.tool_data.length > 0;
  const localHasToolData =
    Array.isArray(local.tool_data) && local.tool_data.length > 0;
  if (!remoteHasToolData && localHasToolData) {
    return { ...remote, tool_data: local.tool_data };
  }
  return remote;
};

const mergeMessageLists = (
  localMessages: IMessage[],
  remoteMessages: IMessage[],
): IMessage[] => {
  const messageMap = new Map<string, IMessage>();

  // Start with local messages
  localMessages.forEach((msg) => messageMap.set(msg.id, msg));

  // Merge with remote messages - prefer remote for existing messages
  remoteMessages.forEach((msg) => {
    const existing = messageMap.get(msg.id);
    if (!existing) {
      // New message from remote
      messageMap.set(msg.id, msg);
    } else if (existing.optimistic) {
      // Always replace optimistic messages with remote versions
      messageMap.set(msg.id, msg);
    } else if (existing.status === "sending") {
      // CRITICAL: Only preserve "sending" status if we are ACTUALLY streaming this conversation.
      // If we are not streaming (e.g. page refresh, crash recovery), this "sending" status is stale
      // and MUST be overwritten by the remote message (which has the true final state).
      if (isConversationStreamingNow(existing.conversationId)) {
        return;
      }
      // Not streaming? Clean up stale "sending" message by overwriting from remote,
      // but keep locally-retained tool cards the backend copy may still be missing.
      messageMap.set(msg.id, withPreservedToolData(msg, existing));
    } else {
      // Message exists locally
      // ALWAYS prefer remote version for synced messages to ensure consistency
      // Local timestamps might be drifted or ahead due to optimistic updates (like on abort)
      // The only exception is 'sending' status handled above
      messageMap.set(msg.id, withPreservedToolData(msg, existing));
    }
  });

  // No orphan sweep: the client's send id IS the persisted message id (single
  // identity), so the server copy lands on the same key above and replaces the
  // optimistic record naturally. Sends the server never received stay visible
  // and are marked failed by the turn lifecycle — never silently deleted.

  // Convert back to array and sort by creation time
  return Array.from(messageMap.values()).toSorted(
    (a, b) => a.createdAt.getTime() - b.createdAt.getTime(),
  );
};

const mapApiMessagesToStored = (
  messages: MessageType[],
  conversationId: string,
): IMessage[] =>
  messages.map((message, index) => {
    const createdAt = message.date ? new Date(message.date) : new Date();
    const role = mapMessageRole(message.type);
    const messageId =
      message.message_id || `${conversationId}-${index}-${createdAt.getTime()}`;

    return {
      id: messageId,
      conversationId,
      content: message.response,
      role,
      status: message.loading ? "sending" : "sent",
      createdAt,
      updatedAt: createdAt,
      messageId: message.message_id,
      fileIds: message.fileIds,
      fileData: message.fileData,
      toolName: message.selectedTool ?? null,
      toolCategory: message.toolCategory ?? null,
      workflowId: message.selectedWorkflow?.id ?? null,
      follow_up_actions: message.follow_up_actions ?? null,
      image_data: message.image_data ?? null,
      isConvoSystemGenerated: message.isConvoSystemGenerated,
      error: message.error ?? null,
      memory_data: message.memory_data ?? null,
      tool_data: message.tool_data ?? null,
      todo_progress: message.todo_progress ?? null,
      pinned: message.pinned ?? false,
      selectedCalendarEvent: message.selectedCalendarEvent,
      selectedWorkflow: message.selectedWorkflow,
      replyToMessageId: message.replyToMessage?.id ?? null,
      replyToMessageData: message.replyToMessage ?? null,
    } satisfies IMessage;
  });

const mapMessageRole = (
  role: MessageType["type"],
): "user" | "assistant" | "system" => {
  switch (role) {
    case "user":
      return "user";
    case "bot":
      return "assistant";
    default:
      return "system";
  }
};

const toTimestamp = (timestamp?: string): number => {
  if (!timestamp) return 0;
  const parsed = new Date(timestamp);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const identifyStaleConversations = (
  localConversations: IConversation[],
  remoteConversations: Conversation[],
): ConversationSyncItem[] => {
  const localMap = new Map(
    localConversations.map((conv) => [
      conv.id,
      conv.updatedAt || conv.createdAt,
    ]),
  );

  const staleItems: ConversationSyncItem[] = [];

  for (const remote of remoteConversations) {
    const conversationId = remote.conversation_id;
    const localUpdatedAt = localMap.get(conversationId);

    if (!localUpdatedAt) {
      // New conversation not in local DB
      staleItems.push({
        conversation_id: conversationId,
        last_updated: undefined,
      });
      continue;
    }

    const remoteUpdated = toTimestamp(remote.updatedAt ?? remote.createdAt);
    const localUpdated = localUpdatedAt.getTime();

    if (remoteUpdated > localUpdated) {
      // Remote is newer than local
      staleItems.push({
        conversation_id: conversationId,
        last_updated: localUpdatedAt.toISOString(),
      });
    }
  }

  return staleItems;
};

/**
 * Identifies conversations that exist locally but were deleted on the server.
 * Applies safety filters to avoid aggressive deletion:
 * 1. Only considers conversations older than MIN_AGE_FOR_DELETION_MS
 * 2. Skips conversations that are currently being streamed
 * 3. Only checks against conversations within the sync window (first page of results)
 */
const identifyDeletedConversations = (
  localConversations: IConversation[],
  remoteConversations: Conversation[],
): string[] => {
  // Safety threshold: Only delete conversations that haven't been updated locally in the last X minutes
  // This prevents accidental deletion of conversations that might not be in the first page of API results
  // Reduced to 5 minutes to clean up deleted conversations faster while still providing a safety buffer for new creations
  const DELETED_CONVERSATION_SAFETY_MINUTES = 5;

  // Minimum age for a local conversation to be considered for deletion (in milliseconds)
  const MIN_AGE_FOR_DELETION_MS =
    DELETED_CONVERSATION_SAFETY_MINUTES * 60 * 1000;
  const remoteIds = new Set(remoteConversations.map((c) => c.conversation_id));
  const deletedIds: string[] = [];

  for (const local of localConversations) {
    // Skip if conversation exists on remote
    if (remoteIds.has(local.id)) continue;

    // Skip if conversation is currently being streamed
    if (isConversationStreamingNow(local.id)) continue;

    // Safety: Skip recently created/updated conversations
    // They might not have synced to the server yet, or might be beyond the pagination limit
    const localUpdateTime = (local.updatedAt || local.createdAt).getTime();
    const ageMs = Date.now() - localUpdateTime;

    if (ageMs < MIN_AGE_FOR_DELETION_MS) {
      continue;
    }

    deletedIds.push(local.id);
  }

  return deletedIds;
};

// DEBUG: surface cases where a sync merge would blank out or shrink an existing
// bot message's content — the symptom of a known overwrite bug.
const warnOnMessageContentLoss = (
  mergedMessages: IMessage[],
  localMessages: IMessage[],
  conversationId: string,
): void => {
  for (const merged of mergedMessages) {
    if (merged.role !== "assistant") continue;

    const local = localMessages.find((m) => m.id === merged.id);
    if (local?.content && !merged.content) {
      console.warn(
        `[SyncService] ⚠️ SYNC OVERWRITING bot message ${merged.id} — local has content (${local.content.length} chars), merged is EMPTY. This is the bug!`,
        {
          conversationId,
          localStatus: local.status,
          mergedStatus: merged.status,
        },
      );
    }
    if (
      local?.content &&
      merged.content &&
      local.content.length > merged.content.length
    ) {
      console.warn(
        `[SyncService] ⚠️ SYNC SHRINKING bot message ${merged.id} — local: ${local.content.length} chars, merged: ${merged.content.length} chars`,
        { conversationId },
      );
    }
  }
};

export const batchSyncConversations = async (): Promise<void> => {
  // CRITICAL: Skip sync while any turn streams to prevent data corruption —
  // per-conversation guards above handle the fine-grained cases, this is the
  // conservative whole-batch gate.
  if (hasAnyLiveTurn()) {
    console.debug("[SyncService] Skipped — stream in progress");
    return;
  }
  console.debug("[SyncService] Starting batch sync (stream not active)");

  try {
    const [remoteConversations, localConversations] = await Promise.all([
      chatApi
        .fetchConversations(1, MAX_SYNC_CONVERSATIONS)
        .then((res) => res.conversations),
      db.getAllConversations(),
    ]);

    if (remoteConversations.length === 0) {
      return;
    }

    const staleItems = identifyStaleConversations(
      localConversations,
      remoteConversations,
    );

    // Identify conversations that exist locally but were deleted on the server
    const deletedConversationIds = identifyDeletedConversations(
      localConversations,
      remoteConversations,
    );

    if (deletedConversationIds.length > 0) {
      // Delete conversations that no longer exist on the server
      await db.deleteConversationsAndMessagesBulk(deletedConversationIds);
    }

    if (staleItems.length === 0) {
      return;
    }

    const { conversations: freshConversations } =
      await chatApi.batchSyncConversations(staleItems);

    if (freshConversations.length === 0) {
      return;
    }

    await Promise.allSettled(
      freshConversations.map(async (conversation) => {
        const conversationId = conversation.conversation_id;
        const messages = conversation.messages ?? [];
        const artifacts = conversation.artifacts ?? [];

        // Skip syncing if streaming or pending save (e.g., after abort)
        if (shouldBlockSyncForConversation(conversationId)) return;

        const mappedConversation: IConversation = {
          id: conversationId,
          title: conversation.description || "Untitled conversation",
          description: conversation.description,
          starred: conversation.starred ?? false,
          isSystemGenerated: conversation.is_system_generated ?? false,
          isOnboardingConversation:
            conversation.is_onboarding_conversation ?? false,
          systemPurpose: conversation.system_purpose ?? null,
          isUnread: conversation.is_unread ?? false,
          artifacts,
          createdAt: new Date(conversation.createdAt),
          updatedAt: conversation.updatedAt
            ? new Date(conversation.updatedAt)
            : new Date(conversation.createdAt),
        };

        // Refresh the runtime lookup map from the server-authoritative registry.
        // Safe from clobbering a live turn: streaming conversations are filtered
        // out by shouldBlockSync above.
        useChatStore
          .getState()
          .setConversationArtifacts(conversationId, artifacts);

        const remoteMessages = mapApiMessagesToStored(messages, conversationId);
        const localMessages =
          await db.getMessagesForConversation(conversationId);
        const mergedMessages = mergeMessageLists(localMessages, remoteMessages);

        warnOnMessageContentLoss(mergedMessages, localMessages, conversationId);

        await Promise.allSettled([
          db.putConversation(mappedConversation),
          messages.length > 0
            ? db.syncMessages(conversationId, mergedMessages)
            : Promise.resolve(),
        ]);
      }),
    );
  } catch {
    // Ignore background sync errors to avoid impacting the UI
  }
};

/**
 * Merge an already-fetched conversation payload into IndexedDB. No-op while
 * the conversation streams or a save is pending — a live turn's messages must
 * never be overwritten by the server's not-yet-persisted copy.
 */
export const applySyncedConversation = async (
  conversationId: string,
  conversation: SyncedConversation,
): Promise<void> => {
  if (shouldBlockSyncForConversation(conversationId)) {
    return;
  }

  const messages = conversation.messages ?? [];
  const artifacts = conversation.artifacts ?? [];

  // Map conversation to IndexedDB format
  const mappedConversation: IConversation = {
    id: conversationId,
    title: conversation.description || "Untitled conversation",
    description: conversation.description,
    starred: conversation.starred ?? false,
    isSystemGenerated: conversation.is_system_generated ?? false,
    isOnboardingConversation: conversation.is_onboarding_conversation ?? false,
    systemPurpose: conversation.system_purpose ?? null,
    isUnread: conversation.is_unread ?? false,
    artifacts,
    createdAt: new Date(conversation.createdAt),
    updatedAt: conversation.updatedAt
      ? new Date(conversation.updatedAt)
      : new Date(conversation.createdAt),
  };

  // Refresh the runtime lookup map from the server-authoritative registry. The
  // store stamps session_id onto each entry so artifact fetch URLs resolve.
  useChatStore.getState().setConversationArtifacts(conversationId, artifacts);

  // Map messages
  const remoteMessages = mapApiMessagesToStored(messages, conversationId);
  const localMessages = await db.getMessagesForConversation(conversationId);

  const mergedMessages = mergeMessageLists(localMessages, remoteMessages);

  // Persist to IndexedDB
  await Promise.allSettled([
    db.putConversation(mappedConversation),
    messages.length > 0
      ? db.syncMessages(conversationId, mergedMessages)
      : Promise.resolve(),
  ]);

  console.debug(
    `[SyncService] Synced conversation ${conversationId} with ${messages.length} messages`,
  );
};

/**
 * Sync a single conversation from backend to IndexedDB.
 * Used after stream cancellation to ensure local data matches backend.
 */
export const syncSingleConversation = async (
  conversationId: string,
): Promise<void> => {
  // Skip sync if streaming or pending save to prevent overwriting in-flight data
  if (shouldBlockSyncForConversation(conversationId)) {
    return;
  }

  try {
    // Use batch sync with a single conversation item
    // Sending undefined for last_updated forces backend to return the full conversation
    const { conversations: freshConversations } =
      await chatApi.batchSyncConversations([
        { conversation_id: conversationId, last_updated: undefined },
      ]);

    if (freshConversations.length === 0) {
      return;
    }

    await applySyncedConversation(conversationId, freshConversations[0]);
  } catch (error) {
    console.error(
      `[SyncService] Failed to sync conversation ${conversationId}:`,
      error,
    );
  }
};
